import type { AttributeField } from "@/features/properties/types/propertyAttributes";

/**
 * Strips the [section] suffix from a question title and lowercases it —
 * same normalization as useTemplateOptions.cleanTitle, duplicated here so
 * this module stays free of the supabase/firebase import chain (testable).
 */
function cleanTitle(title: string): string {
  return title.replace(/\s*\[.*?\]\s*$/, "").trim().toLowerCase();
}

type LeafRecord = Record<string, unknown> & { title: string; type: string };

export interface MergeStats {
  /** Non-required leaves filled from the optional-source survey. */
  merged: number;
  /** Non-required leaves with no match in the optional-source survey. */
  unmatched: number;
}

/** Answer-bearing props copied from the optional-source survey's leaves. */
const LEAF_PAYLOAD_KEYS = ["value", "notes", "evidence", "confidence"] as const;

function leafKey(field: LeafRecord): string {
  const refId = typeof field.ref_id === "string" && field.ref_id ? field.ref_id : null;
  const id = typeof field.id === "string" && field.id ? field.id : null;
  return refId ?? id ?? `title:${cleanTitle(field.title)}`;
}

function collectLeaves(
  fields: AttributeField[],
  into: Map<string, LeafRecord>,
  ambiguousTitles: Set<string>
): void {
  for (const field of fields) {
    if (field.type === "node") {
      collectLeaves(field.fields ?? [], into, ambiguousTitles);
    } else {
      const record = field as unknown as LeafRecord;
      into.set(leafKey(record), record);
      // Title fallback for fields whose ids drifted between the two surveys.
      // Cleaned titles can collide across sections (the disambiguating
      // [section] suffix is stripped) — an ambiguous title must match
      // nothing rather than silently resolve to the last field seen.
      const titleKey = `title:${cleanTitle(record.title)}`;
      if (ambiguousTitles.has(titleKey)) continue;
      const existing = into.get(titleKey);
      if (existing && existing !== record) {
        into.delete(titleKey);
        ambiguousTitles.add(titleKey);
      } else {
        into.set(titleKey, record);
      }
    }
  }
}

/**
 * Merge two surveys with identical structure into a new data tree:
 * required questions (per the template's required set, keyed by cleaned
 * lowercase title) keep the base survey's answers — the base is the
 * field-app survey — while every other question takes its answer (value,
 * notes, evidence, confidence) from the optional-source (web) survey.
 */
export function mergeSurveyData(
  baseFields: AttributeField[],
  optionalSourceFields: AttributeField[],
  requiredSet: Set<string>
): { fields: AttributeField[]; stats: MergeStats } {
  const optionalLeaves = new Map<string, LeafRecord>();
  collectLeaves(optionalSourceFields, optionalLeaves, new Set());

  const stats: MergeStats = { merged: 0, unmatched: 0 };

  const walk = (fields: AttributeField[]): AttributeField[] =>
    fields.map((field) => {
      if (field.type === "node") {
        return { ...field, fields: walk(field.fields ?? []) } as AttributeField;
      }

      const record = field as unknown as LeafRecord;
      if (requiredSet.has(cleanTitle(record.title))) {
        // Required → field-app answer, copied as-is from the base.
        return { ...field } as AttributeField;
      }

      const source =
        optionalLeaves.get(leafKey(record)) ??
        optionalLeaves.get(`title:${cleanTitle(record.title)}`);
      if (!source) {
        stats.unmatched += 1;
        return { ...field } as AttributeField;
      }

      stats.merged += 1;
      const next: Record<string, unknown> = { ...field };
      for (const key of LEAF_PAYLOAD_KEYS) {
        if (key in source) {
          next[key] = source[key];
        } else {
          delete next[key];
        }
      }
      return next as unknown as AttributeField;
    });

  return { fields: walk(baseFields), stats };
}
